import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { toast } from "sonner";
import { FileText, Download, Filter, Users, CircleDashed, CircleCheck } from "lucide-react";

/**
 * SubmissionsArchive — reads `/api/reports/weekly-submissions` and renders
 * every Monday's uploaded weekly / social calendar PDF grouped by TEAM and
 * then by WEEK_START. Each slot (report / plan) gets its own download button
 * that streams the stored PDF via `/api/reports/weekly-submissions/{id}/download?slot=`.
 *
 * Admins see every team; managers see only their own (backend enforces the
 * same ACL — we render whatever the API returns).
 */
export default function SubmissionsArchive() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/weekly-submissions");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load submissions");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const teams = useMemo(() => Array.from(new Set(rows.map((r) => r.team).filter(Boolean))).sort(), [rows]);

  // Filter rows in-memory (small dataset — ~4-10 rows/week × ~52 weeks).
  const visible = useMemo(() => rows.filter((r) => {
    if (teamFilter !== "all" && r.team !== teamFilter) return false;
    if (groupFilter !== "all" && r.submission_group !== groupFilter) return false;
    return true;
  }), [rows, teamFilter, groupFilter]);

  // Group by team → week_start (newest first).
  const grouped = useMemo(() => {
    const byTeam = {};
    for (const r of visible) {
      byTeam[r.team] = byTeam[r.team] || {};
      byTeam[r.team][r.week_start] = byTeam[r.team][r.week_start] || [];
      byTeam[r.team][r.week_start].push(r);
    }
    return byTeam;
  }, [visible]);

  const downloadSlot = async (row, slot) => {
    try {
      const res = await api.get(`/reports/weekly-submissions/${row.id}/download?slot=${slot}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.uploads[slot]?.file_name || `${row.submission_group}-${slot}-${row.week_start}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download");
    }
  };

  return (
    <div className="glass rounded-xl p-4 sm:p-5 mt-6" data-testid="submissions-archive">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="overline flex items-center gap-2"><FileText size={12} /> Submitted PDFs</div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tighter mt-1">Weekly submission archive</h2>
          <p className="text-xs text-white/50 mt-0.5">Every PDF a manager has uploaded — grouped by team and week. Also lands in the shared Weekly Reports chat room.</p>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest">
          <Filter size={12} className="text-white/40" />
          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            data-testid="submissions-team-filter"
            className="bg-black/40 border border-white/10 rounded-full h-8 px-3 hover:border-white/20"
          >
            <option value="all">All teams</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            data-testid="submissions-group-filter"
            className="bg-black/40 border border-white/10 rounded-full h-8 px-3 hover:border-white/20"
          >
            <option value="all">All types</option>
            <option value="weekly">Weekly</option>
            <option value="social">Social</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-white/40 font-mono">Loading…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-xs text-white/40 font-mono text-center py-6" data-testid="submissions-archive-empty">
          No PDFs uploaded yet — managers will land them here as they submit each Monday.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([team, weeks]) => (
            <div key={team} data-testid={`archive-team-${team}`}>
              <div className="flex items-center gap-2 mb-2">
                <Users size={12} className="text-red-400" />
                <div className="text-xs uppercase tracking-widest text-white/70 font-mono">{team}</div>
                <div className="h-px flex-1 bg-white/10" />
              </div>
              <div className="space-y-2">
                {Object.entries(weeks)
                  .sort(([a], [b]) => (a < b ? 1 : -1))
                  .map(([week, list]) => (
                    <div key={week} className="rounded-lg border border-white/10 bg-black/25 p-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono shrink-0">Week of</span>
                          <span className="text-sm text-white font-mono">{week}</span>
                        </div>
                        <span className="text-[0.7rem] font-mono uppercase tracking-widest text-white/40 shrink-0">
                          {list.length} submission{list.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {list.map((row) => {
                          const r = row.uploads.report;
                          const p = row.uploads.plan;
                          const done = r && p;
                          return (
                            <div key={row.id} className="rounded-md border border-white/5 bg-black/30 p-2.5" data-testid={`archive-row-${row.id}`}>
                              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                <div className="min-w-0">
                                  <div className="text-sm text-white/90 truncate">
                                    <span className="text-[0.7rem] uppercase tracking-widest font-mono text-amber-300 mr-2">{row.submission_group}</span>
                                    {row.assignee_name || "Unassigned"}
                                  </div>
                                </div>
                                <span className={`inline-flex items-center gap-1 text-[0.7rem] uppercase tracking-widest font-mono ${done ? "text-emerald-400" : "text-amber-300"}`}>
                                  {done
                                    ? (<><CircleCheck size={11} /> Complete</>)
                                    : (<><CircleDashed size={11} /> {Object.values(row.uploads).filter(Boolean).length}/2</>)}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {[
                                  { key: "report", label: "Last week's report", u: r },
                                  { key: "plan",   label: "This week's plan",   u: p },
                                ].map((slot) => (
                                  <button
                                    key={slot.key}
                                    onClick={() => slot.u && downloadSlot(row, slot.key)}
                                    disabled={!slot.u}
                                    data-testid={`archive-download-${row.id}-${slot.key}`}
                                    className={`flex items-center justify-between gap-2 rounded border px-2.5 py-1.5 text-left transition ${
                                      slot.u
                                        ? "border-emerald-500/30 bg-emerald-500/[0.06] hover:bg-emerald-500/[0.12]"
                                        : "border-white/10 bg-black/40 opacity-40 cursor-not-allowed"
                                    }`}
                                    title={slot.u ? `Download ${slot.u.file_name}` : "Not uploaded yet"}
                                  >
                                    <div className="min-w-0">
                                      <div className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono">{slot.label}</div>
                                      <div className="text-xs text-white/80 truncate">{slot.u?.file_name || "— pending —"}</div>
                                    </div>
                                    <Download size={13} className="text-white/60 shrink-0" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
